import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  Add, 
  Delete, 
  ExpandMore,
  Security,
  Code,
  Info
} from '@mui/icons-material';

const AdvancedSettingsTab = ({ formData, onInputChange, onHeaderChange }) => {
  const [newHeaderKey, setNewHeaderKey] = useState('');
  const [newHeaderValue, setNewHeaderValue] = useState('');

  const commonHeaders = [
    { key: 'User-Agent', description: 'Идентификация браузера/приложения' },
    { key: 'Referer', description: 'Ссылка на источник запроса' },
    { key: 'Origin', description: 'Домен источника запроса' },
    { key: 'Authorization', description: 'Токен авторизации' },
    { key: 'X-Forwarded-For', description: 'IP адрес клиента' },
    { key: 'Accept', description: 'Принимаемые типы контента' }
  ];

  const addHeader = () => {
    if (newHeaderKey && newHeaderValue) {
      onHeaderChange(newHeaderKey, newHeaderValue);
      setNewHeaderKey('');
      setNewHeaderValue('');
    }
  };

  const removeHeader = (headerKey) => {
    onHeaderChange(headerKey, '');
  };

  const addCommonHeader = (headerKey) => {
    onHeaderChange(headerKey, '');
  };

  const activeHeaders = Object.entries(formData.headers || {}).filter(([key, value]) => value !== '');

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Дополнительные настройки
      </Typography>

      <Grid container spacing={3}>
        {/* HTTP Headers */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Security color="primary" />
                HTTP Headers
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                HTTP заголовки используются для передачи дополнительной информации при запросах к потоку
              </Alert>

              {/* Активные заголовки */}
              {activeHeaders.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Активные заголовки:
                  </Typography>
                  {activeHeaders.map(([key, value]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip label={key} size="small" color="primary" />
                      <TextField
                        size="small"
                        value={value}
                        onChange={(e) => onHeaderChange(key, e.target.value)}
                        placeholder="Значение заголовка"
                        sx={{ flexGrow: 1 }}
                      />
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => removeHeader(key)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Добавление нового заголовка */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Добавить заголовок</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      size="small"
                      label="Название заголовка"
                      value={newHeaderKey}
                      onChange={(e) => setNewHeaderKey(e.target.value)}
                      placeholder="Content-Type"
                    />
                    <TextField
                      size="small"
                      label="Значение"
                      value={newHeaderValue}
                      onChange={(e) => setNewHeaderValue(e.target.value)}
                      placeholder="application/json"
                      sx={{ flexGrow: 1 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={addHeader}
                      disabled={!newHeaderKey || !newHeaderValue}
                    >
                      Добавить
                    </Button>
                  </Box>

                  <Typography variant="subtitle2" gutterBottom>
                    Часто используемые заголовки:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {commonHeaders.map(({ key, description }) => (
                      <Chip
                        key={key}
                        label={key}
                        size="small"
                        variant="outlined"
                        clickable
                        onClick={() => addCommonHeader(key)}
                        title={description}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Grid>

        {/* JSON Preview */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Code color="primary" />
                JSON Предварительный просмотр
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                Так будет выглядеть канал в API ответе
              </Alert>

              <Box
                component="pre"
                sx={{
                  backgroundColor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  overflow: 'auto',
                  maxHeight: 300
                }}
              >
                {JSON.stringify({
                  id: 'example-id',
                  url: formData.url || 'channel-url',
                  name: formData.name || 'Channel Name',
                  icon: formData.icon || 'https://example.com/icon.png',
                  frame: formData.frame || '',
                  stream: formData.stream || 'https://example.com/stream.m3u8',
                  stream_redirect: formData.stream_redirect || '',
                  headers: formData.headers,
                  is_active: formData.is_active,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                }, null, 2)}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Справочная информация */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info color="primary" />
                Справочная информация
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="subtitle2" gutterBottom>
                Описание полей:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">URL:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Уникальный идентификатор канала, используется в API
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="bold">Stream:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Прямая ссылка на видеопоток (HLS, DASH, RTMP, MP4)
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="bold">Frame:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ссылка на встроенный плеер или iframe для воспроизведения
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="bold">Headers:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    HTTP заголовки для запросов к потоку (авторизация, User-Agent и т.д.)
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdvancedSettingsTab;
